import { useState } from 'react';
import { Link } from 'react-router-dom';
import { env } from '../../../config/env';
import { JobCardEditorPopover } from '../../../components/admin/inline-editor';
import { useAuth } from '../../../hooks/useAuth';
import { useSaveJob, useUnsaveJob } from '../../../hooks/unified/useSavedJobsQueries';
import type { Job } from '../../../types/job';
import type { Job as UnifiedJob } from '../../../services/unified/types';

// Strip markdown syntax for plain text preview
const stripMarkdown = (text: string): string => {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/#{1,6}\s+/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/>\s+/g, '')
    .replace(/\n{2,}/g, ' ')
    .replace(/\n/g, ' ')
    .trim();
};

// Important keywords to highlight in job summaries (tech skills, roles, academic, etc.)
const HIGHLIGHT_KEYWORDS = [
  // Programming & Tech
  'javascript', 'typescript', 'python', 'java', 'rust', 'go', 'ruby', 'php', 'swift', 'kotlin',
  'react', 'angular', 'vue', 'node', 'express', 'django', 'flask', 'spring', 'rails',
  'aws', 'azure', 'gcp', 'docker', 'kubernetes', 'devops', 'cloud', 'microservices',
  'machine learning', 'ai', 'data science', 'analytics', 'tensorflow', 'pytorch',
  'mongodb', 'postgresql', 'mysql', 'redis', 'elasticsearch',
  // Roles
  'senior', 'lead', 'architect', 'manager', 'director', 'engineer', 'developer',
  'designer', 'analyst', 'consultant', 'specialist', 'coordinator', 'teacher', 'faculty',
  'full-stack', 'frontend', 'backend', 'fullstack', 'mobile', 'web', 'software',
  'agile', 'scrum', 'remote', 'hybrid', 'startup', 'fintech', 'healthcare', 'education',
  // Academic & Exams (India)
  'jee', 'neet', 'upsc', 'gate', 'cat', 'gmat', 'gre', 'ielts', 'toefl', 'sat',
  'iit', 'nit', 'aiims', 'cbse', 'icse', 'ncert', 'olympiad', 'kvpy', 'ntse',
  'physics', 'chemistry', 'mathematics', 'biology', 'english', 'hindi', 'science',
  'tutor', 'coaching', 'mentor', 'instructor', 'professor', 'lecturer', 'trainer',
  'curriculum', 'syllabus', 'exam', 'test', 'assessment', 'preparation', 'prep',
  // Youth & Programs
  'youth', 'student', 'school', 'college', 'university', 'program', 'centre', 'center',
];

// Extract keywords from title that match our highlight list
const extractKeywords = (title: string): string[] => {
  const titleLower = title.toLowerCase();
  const matched: string[] = [];
  for (const keyword of HIGHLIGHT_KEYWORDS) {
    if (titleLower.includes(keyword.toLowerCase())) {
      matched.push(keyword);
    }
  }
  return matched.slice(0, 6);
};

// Highlight keywords in text with primary brand color
const highlightKeywords = (text: string, keywords: string[]): React.ReactNode => {
  if (!keywords.length || !text) return text;
  const words = text.split(/(\s+)/);
  return words.map((word, index) => {
    const wordLower = word.toLowerCase().replace(/[^a-z0-9]/g, '');
    const isKeyword = keywords.some(k => wordLower.includes(k.toLowerCase()));
    if (isKeyword && word.trim()) {
      return (
        <span key={index} className="bg-primary-100 text-primary-700 px-1 rounded font-medium">
          {word}
        </span>
      );
    }
    return word;
  });
};

interface JobCardProps {
  job: Job | UnifiedJob;
  onBookmark?: (jobId: string) => void;
  onShare?: (jobId: string) => void;
}


export function JobCard({ job, onBookmark, onShare }: JobCardProps) {
  const { isAdmin, isAuthenticated } = useAuth();
  const [isEditorOpen, setEditorOpen] = useState(false);
  const saveJobMutation = useSaveJob();
  const unsaveJobMutation = useUnsaveJob();

  const getImageUrl = (imageUrl: string | null | undefined): string | null => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    return `${env.API_BASE_URL}${imageUrl}`;
  };

  const isBookmarkedProp = 'isBookmarked' in job ? job.isBookmarked : false;
  const [isBookmarked, setIsBookmarked] = useState(isBookmarkedProp || false);
  const [isBookmarking, setIsBookmarking] = useState(false);
  const [imageError, setImageError] = useState(false);

  const formatSalary = (salaryMin?: number, salaryMax?: number) => {
    if (salaryMin !== undefined && salaryMax !== undefined) {
      const formatNum = (num: number) => {
        if (num >= 100000) return `${(num / 100000).toFixed(1)}L`;
        if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
        return num.toLocaleString();
      };
      return `₹${formatNum(salaryMin)} - ₹${formatNum(salaryMax)}`;
    }
    return null;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays <= 1) return 'Today';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)}w ago`;
    return `${Math.ceil(diffDays / 30)}mo ago`;
  };

  const handleBookmark = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isBookmarking || !isAuthenticated) return;
    
    setIsBookmarking(true);
    try {
      if (isBookmarked) {
        await unsaveJobMutation.mutateAsync(job.id);
        setIsBookmarked(false);
      } else {
        await saveJobMutation.mutateAsync(job.id);
        setIsBookmarked(true);
      }
      onBookmark?.(job.id);
    } catch (error) {
      // Revert on error
      console.error('Failed to save/unsave job:', error);
    } finally {
      setIsBookmarking(false);
    }
  };

  const handleShare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onShare?.(job.id);
  };

  // Extract fields with fallbacks
  const companyLogoUrl = 'company_logo_url' in job ? job.company_logo_url : 'companyLogo' in job ? job.companyLogo : undefined;
  const salaryMin = 'salary_min' in job ? job.salary_min : undefined;
  const salaryMax = 'salary_max' in job ? job.salary_max : undefined;
  const jobType = 'job_type' in job ? job.job_type : 'type' in job ? job.type : undefined;
  const experienceLevel = 'experience_level' in job ? job.experience_level : 'experience' in job ? job.experience : undefined;
  const postedDate = 'postedDate' in job ? job.postedDate : 'created_at' in job ? job.created_at : undefined;
  const featured = 'featured' in job ? job.featured : false;
  const salary = formatSalary(salaryMin, salaryMax);


  return (
    <Link
      to={`/jobs/${job.id}`}
      className="group block bg-white rounded-2xl border border-gray-200 hover:border-primary-300 hover:shadow-xl transition-all duration-300 overflow-hidden min-h-[420px] sm:min-h-[480px] flex flex-col"
    >
      <div className="p-6 flex flex-col flex-1">
        {/* Header Row */}
        <div className="flex items-start gap-4 mb-5">
          {/* Company Logo */}
          <div className="flex-shrink-0">
            {companyLogoUrl && !imageError ? (
              <img
                src={getImageUrl(companyLogoUrl)!}
                alt={job.company || 'Company'}
                className="w-20 h-20 rounded-xl object-cover bg-gray-50 border border-gray-100"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white font-bold text-3xl shadow-sm">
                {(job.company || 'C')[0].toUpperCase()}
              </div>
            )}
          </div>

          {/* Title & Company */}
          <div className="flex-1 min-w-0 pt-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h3 className="text-base sm:text-xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-tight">
                  {job.title}
                </h3>
                <p className="text-xs text-gray-400 mt-1.5">{job.company}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {isAdmin && (
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditorOpen(true); }}
                    className="flex-shrink-0 opacity-40 hover:opacity-100 transition-all duration-200 bg-purple-600 hover:bg-purple-700 rounded-full p-1.5 shadow-lg hover:scale-110"
                    title="Edit with AI"
                  >
                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </button>
                )}
                {featured && (
                  <span className="flex-shrink-0 px-2.5 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">Featured</span>
                )}
              </div>
            </div>
          </div>
        </div>


        {/* Meta Info Row */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
          {job.location && (
            <span className="inline-flex items-center text-gray-600 text-xs sm:text-sm">
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-1.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {job.location}
            </span>
          )}
          {jobType && (
            <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-primary-50 text-primary-700 text-xs sm:text-sm font-medium rounded-full">
              {jobType.replace('-', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
            </span>
          )}
          {experienceLevel && (
            <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-blue-50 text-blue-700 text-xs sm:text-sm font-medium rounded-full">
              {experienceLevel.charAt(0).toUpperCase() + experienceLevel.slice(1)}
            </span>
          )}
        </div>

        {/* Summary - AI-generated preview with keyword highlighting */}
        <div className="flex-1 min-h-0 overflow-hidden mb-3 sm:mb-4">
          {(('summary' in job && job.summary) || job.description) && (
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed line-clamp-5 sm:line-clamp-6">
              {(() => {
                const summaryText = 'summary' in job && job.summary ? job.summary : stripMarkdown(job.description || '');
                const keywords = extractKeywords(job.title);
                return highlightKeywords(summaryText, keywords);
              })()}
            </p>
          )}
        </div>


        {/* Footer */}
        <div className="pt-4 border-t border-gray-100 mt-auto space-y-2 sm:space-y-0">
          {/* Mobile: Two rows | Desktop: Single row */}
          <div className="flex items-center justify-between sm:hidden">
            {/* Row 1: Salary + Like */}
            <div className="flex items-center gap-2">
              {salary && <span className="text-base font-semibold text-gray-900">{salary}</span>}
            </div>
            <button
              onClick={handleBookmark}
              disabled={isBookmarking}
              className={`w-9 h-9 flex items-center justify-center rounded-xl transition-all duration-200 ${isBookmarked ? 'text-red-500 bg-red-50 hover:bg-red-100' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}
              title={isBookmarked ? 'Saved' : 'Save'}
            >
              <svg className="w-5 h-5" fill={isBookmarked ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </button>
          </div>
          <div className="flex items-center justify-between sm:hidden">
            {/* Row 2: Date + Share + View Job */}
            <span className="text-xs text-gray-400">{formatDate(postedDate)}</span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleShare}
                className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200"
                title="Share"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
              </button>
              <span className="h-9 px-4 flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium rounded-xl transition-all duration-200">
                View Job
              </span>
            </div>
          </div>
          
          {/* Desktop: Original single row layout */}
          <div className="hidden sm:flex items-center justify-between">
            <div className="flex items-center gap-4">
              {salary && <span className="text-lg font-semibold text-gray-900">{salary}</span>}
              <span className="text-sm text-gray-400">{formatDate(postedDate)}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleShare}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200"
                title="Share"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
              </button>
              <button
                onClick={handleBookmark}
                disabled={isBookmarking}
                className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all duration-200 ${isBookmarked ? 'text-red-500 bg-red-50 hover:bg-red-100' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}
                title={isBookmarked ? 'Saved' : 'Save'}
              >
                <svg className="w-5 h-5" fill={isBookmarked ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>
              <span className="h-10 px-5 flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-xl transition-all duration-200">
                View Job
              </span>
            </div>
          </div>
        </div>
      </div>

      {isAdmin && (
        <JobCardEditorPopover
          isOpen={isEditorOpen}
          onClose={() => setEditorOpen(false)}
          jobId={job.id.toString()}
          onContentUpdated={() => {}}
        />
      )}
    </Link>
  );
}
